import { useEffect, useRef } from 'react';
import { useGetLoadStatusQuery, useGetMetaQuery } from '../api/portalApi';
import type { PortalLoadStatus } from '../api/types';

const POLL_MS = 1000;

/**
 * While a page waits for `meta`, polls the backend's data-load progress every second. `meta` waits for the whole load
 * (about 1.5–2.5 min after a backend start); when a load seen running finishes, `meta` is fetched again if its
 * request failed meanwhile (a proxy that gave up on it).
 */
export function usePortalLoad(waiting: boolean): PortalLoadStatus | undefined {
  const { data: status } = useGetLoadStatusQuery(undefined, {
    skip: !waiting,
    pollingInterval: POLL_MS,
    skipPollingIfUnfocused: false,
  });
  const { isError, refetch } = useGetMetaQuery(undefined);
  const sawLoad = useRef(false);
  const state = waiting ? status?.state : undefined;

  useEffect(() => {
    if (state === 'loading' || state === 'failed') sawLoad.current = true;
    if (state === 'loaded' && sawLoad.current) {
      sawLoad.current = false;
      if (isError) void refetch();
    }
  }, [state, isError, refetch]);

  return waiting ? status : undefined;
}
